import { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import { Stack } from '@atlaskit/primitives';
import { useEffect, useState } from 'react';

import { PageSection } from '../../../../common/components/page-section';
import { settingsCodeReviewerPath } from '../../../../routes/paths';
import { useAnalyticsWebClient } from '../../../../utils/analytics/context';
import { RovoDevAgentsPageHeader } from '../../components/rovo-dev-agents-page-header';
import { GithubOrganisationList } from '../components/github-organisation-list';
import { GithubRepositoryList } from '../components/github-repository-list';

interface GCViewMode {
	viewKey: 'organisations' | 'repositories';
	selectedOrg: GH.Organisation | null;
}
class Organisations implements GCViewMode {
	viewKey: 'organisations';

	selectedOrg: null;

	constructor() {
		this.viewKey = 'organisations';
		this.selectedOrg = null;
	}
}
class Repositories implements GCViewMode {
	viewKey: 'repositories';

	selectedOrg: GH.Organisation;

	constructor(selectedOrg: GH.Organisation) {
		this.viewKey = 'repositories';
		this.selectedOrg = selectedOrg;
	}
}

export const CodeReviewerGithubConnections = () => {
	const [viewMode, setViewMode] = useState<GCViewMode>(new Organisations());
	const { sendScreenEvent } = useAnalyticsWebClient();

	const setSelectedOrg = (org: GH.Organisation) => {
		setViewMode(new Repositories(org));
	};

	const clearSelectedOrg = () => setViewMode(new Organisations());

	useEffect(() => {
		sendScreenEvent({
			name:
				viewMode.viewKey === 'organisations'
					? 'view-github-organisations'
					: 'view-github-repositories',
		});
	}, [sendScreenEvent, viewMode.viewKey]);

	if (viewMode.viewKey === 'organisations') {
		return (
			<PageSection>
				<Stack space="space.200">
					<RovoDevAgentsPageHeader
						additionalBreadcrumbItems={[
							<BreadcrumbsItem
								href={`/dev-agents${settingsCodeReviewerPath}`}
								text="Set up Code Reviewer for GitHub"
								key="set-up-code-reviewer-for-github"
							/>,
						]}
						headingText="Set up Code Reviewer for GitHub"
						descriptionText="Select the GitHub organizations and repositories you want to enable the Code Reviewer on. When the Code Reviewer is enabled, it automatically review new pull requests and leave feedbacks in pull request comments. You must be a Rovo Dev Agents admin to enable Code Reviewer on a GitHub organization."
					/>
					<GithubOrganisationList setSelectedOrg={setSelectedOrg} />
				</Stack>
			</PageSection>
		);
	}

	if (viewMode.viewKey === 'repositories' && viewMode.selectedOrg) {
		return (
			<PageSection>
				<Stack space="space.300">
					<RovoDevAgentsPageHeader
						additionalBreadcrumbItems={[
							<BreadcrumbsItem
								onClick={() => clearSelectedOrg()}
								href={`/dev-agents${settingsCodeReviewerPath}`}
								text="Set up Code Reviewer for GitHub"
								key="set-up-code-reviewer-for-github"
							/>,
							<BreadcrumbsItem
								text={viewMode.selectedOrg.owner}
								key={viewMode.selectedOrg.owner}
							/>,
						]}
						headingText={`${viewMode.selectedOrg.owner}`}
						descriptionText="
						Select the repositories in your organization you want to enable Code Reviewer on. When
						Code Reviewer is enabled on a repository, it automatically reviews new pull requests and
						leaves feedback in pull request comments. You must be a Rovo Dev Agents admin to enable
						Code Reviewer on a repository.
						"
					/>
					<GithubRepositoryList selectedOrg={viewMode.selectedOrg} />
				</Stack>
			</PageSection>
		);
	}

	// TODO: Replace with empty state
	return <PageSection>Error</PageSection>;
};
